import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { PatientService } from 'src/app/services/patient.service';
import { SnackService } from 'src/app/services/snack.service';
import {
  EditableTierRow,
  SliderConfig,
  SliderRow,
  TierNotification,
  VITAL_FLAG_SLIDER_CONFIGS,
  VitalFlagBloodPressureSecondary,
  VitalFlagCategory,
  VitalFlagComparitors,
  VitalFlagOffsetTypes,
  VitalFlagOperators,
} from '../../models/vital_flags.model';
import { VitalFlagService } from '../../services/vital-flag.service';

@Component({
  selector: 'app-vital-flag-fixed-target',
  templateUrl: './vital-flag-fixed-target.component.html',
  styleUrls: ['./vital-flag-fixed-target.component.scss'],
})
export class VitalFlagFixedTargetComponent implements OnInit {
  @Output() closeEmmiter = new EventEmitter<boolean>();

  isLinear = false;
  firstFormGroup: UntypedFormGroup;
  secondFormGroup: UntypedFormGroup;
  thresholdValid = false;
  saving = false;

  // Used to switch between vertical (mobile) and horizontal stepper layouts
  isExtraSmall$: Observable<BreakpointState> = this.breakpointObserver.observe(Breakpoints.XSmall);

  // Step 3 data — populated when entering the Target step
  sliderRows: SliderRow[] = [];           // used when comparitor = 'fixed'
  editableTierRows: EditableTierRow[] = []; // used when comparitor = 'days' | 'measures'

  // Step 4 data — one entry per unique tier, kept in sync with editableTierRows
  tierNotifications: TierNotification[] = [];

  tierOptions: Array<{ tier: number; shortLabel: string }>;
  vitalFlagBPSecondary = Object.values(VitalFlagBloodPressureSecondary);
  bloodPressureType = VitalFlagCategory.BLOOD_PRESSURE;
  operatorOptions = [
    { value: VitalFlagOperators.LESS_EQUAL, label: 'Below or equal (<=)' },
    { value: VitalFlagOperators.GREATER_EQUAL, label: 'Above or equal (>=)' },
    { value: VitalFlagOperators.LESS, label: 'Below (<)' },
    { value: VitalFlagOperators.GREATER, label: 'Above (>)' },
    { value: VitalFlagOperators.EQUALS, label: 'Equal to (==)' },
  ];

  // Source of truth for tier metadata (label, color) used across the wizard
  private readonly tierDefinitions = [
    { tier: 3, tierLabel: 'Tier 3 - High Risk', tierColor: 'warn' },
    { tier: 2, tierLabel: 'Tier 2 - Medium Risk', tierColor: 'accent' },
    { tier: 1, tierLabel: 'Tier 1 - Low Risk', tierColor: 'primary' },
  ];

  constructor(
    private vitalFlagService: VitalFlagService,
    private fb: UntypedFormBuilder,
    public patientService: PatientService,
    private snackBarService: SnackService,
    private breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit(): void {
    this.tierOptions = [
      { tier: 3, shortLabel: 'Tier 3' },
      { tier: 2, shortLabel: 'Tier 2' },
      { tier: 1, shortLabel: 'Tier 1' },
    ];
    this.initializeForm();
  }

  // True when the user selected 'fixed' as the comparison type in step 2
  get isFixed(): boolean {
    return this.secondFormGroup.get('comparitors').value === 'fixed';
  }

  // Returns 'days' or 'measures' for use in hint labels in the Target step
  get comparitorPeriodLabel(): string {
    const c = this.secondFormGroup?.get('comparitors')?.value;
    return c === 'days' ? 'days' : 'measures';
  }

  initializeForm(): void {
    this.firstFormGroup = this.fb.group({
      type: ['', Validators.required],
      secondary: [false],
    });
    this.secondFormGroup = this.fb.group({
      comparitors: ['', Validators.required],
      comparitor_type: ['number'],
      count: [0],
    });

    this.initTierNotifications();

    // Default the secondary field to the Systolic enum value so
    // Blood Pressure saves the correct secondary flag on the record
    const secondaryValue =
      this.firstFormGroup.get('secondary').value === true
        ? VitalFlagBloodPressureSecondary.DIASTOLIC_BP
        : VitalFlagBloodPressureSecondary.SYSTOLIC_BP;
    this.firstFormGroup.get('secondary').setValue(secondaryValue);
  }

  // Called when the user clicks Next on step 2 — builds the Target step content
  onComparitorEnter(): void {
    this.buildThresholdStep();
  }

  addTierRow(): void {
    this.editableTierRows.push(this.createTierRow(3));
    this.syncTierNotifications();
  }

  removeTierRow(index: number): void {
    this.editableTierRows.splice(index, 1);
    this.syncTierNotifications();
  }

  // Updates tier metadata on a row when the user changes the tier dropdown
  // and refreshes the notifications step
  onTierChange(row: EditableTierRow, newTier: number): void {
    const def = this.tierDefinitions.find((td) => td.tier === newTier);
    if (def) {
      row.tier = def.tier;
      row.tierLabel = def.tierLabel;
      row.tierColor = def.tierColor;
    }
    this.syncTierNotifications();
  }

  // Mutates values in-place to avoid triggering ngOnChanges on the slider component,
  // which would reset thumb positions
  onSliderChange(row: SliderRow, newValues: number[]): void {
    newValues.forEach((v, i) => row.values[i] = v);
    this.validateThresholds();
  }

  // For fixed: all slider thumbs must be strictly ascending.
  // For days/measures: every row must have a non-null offset_amount (0 is valid).
  validateThresholds(): boolean {
    if (this.isFixed) {
      this.thresholdValid = this.sliderRows.length > 0 && this.sliderRows.every((row) => {
        if (row.mode === 'symmetric') {
          return row.values[0] < row.values[1] && row.values[1] < row.values[2] && row.values[2] < row.values[3];
        }
        return row.values[0] < row.values[1];
      });
    } else {
      this.thresholdValid = this.editableTierRows.length > 0 && this.editableTierRows.every((r) => r.offset_amount != null);
    }
    return this.thresholdValid;
  }

  async save(): Promise<void> {
    this.saving = true;
    const type: string = this.firstFormGroup.get('type').value;
    const secondaryFormValue = this.firstFormGroup.get('secondary').value;
    const isSecondaryValueSystolic = secondaryFormValue === VitalFlagBloodPressureSecondary.SYSTOLIC_BP;

    try {
      if (this.isFixed) {
        await this.saveFixedFlags(type, isSecondaryValueSystolic);
      } else {
        await this.saveDaysMeasuresFlags(type, isSecondaryValueSystolic);
      }
      this.snackBarService.genericSnackBar('Vital flags created successfully', ['success-snackbar'], 6000);
      this.closeEmmiter.emit(true);
    } catch (error) {
      console.error('Error creating vital flags', error);
      this.snackBarService.genericSnackBar('Error creating vital flags', ['error-snackbar'], 6000);
    } finally {
      this.saving = false;
    }
  }

  private initTierNotifications(): void {
    this.tierNotifications = this.tierDefinitions.map((td) => ({
      ...td,
      alert_provider: false,
      alert_patient: false,
    }));
  }

  // Rebuilds the notifications list to match the unique tiers currently in editableTierRows.
  // Preserves existing toggle state so changes aren't lost when adding/removing rows.
  private syncTierNotifications(): void {
    const activeTiers = this.isFixed
      ? this.tierDefinitions.map((td) => td.tier)
      : [...new Set(this.editableTierRows.map((r) => r.tier))];

    const existing = new Map(this.tierNotifications.map((tn) => [tn.tier, tn]));
    this.tierNotifications = activeTiers
      .map((tier) => {
        if (existing.has(tier)) {
          return existing.get(tier); // keep previous toggle state
        }
        const def = this.tierDefinitions.find((td) => td.tier === tier);
        return { ...def, alert_provider: false, alert_patient: false };
      })
      .sort((a, b) => b.tier - a.tier);
  }

  // Decides which UI to show in the Target step based on the selected comparitor
  private buildThresholdStep(): void {
    const type: string = this.firstFormGroup.get('type').value;

    if (this.isFixed) {
      this.buildSliderRows(type);
      this.editableTierRows = [];
    } else {
      this.buildEditableTierRows();
      this.sliderRows = [];
    }
    // Reconcile notifications with the active tiers so days/measures only shows
    // notifications for tiers the user has actually configured (Tier 3 by default).
    this.syncTierNotifications();
  }

  private buildSliderRows(type: string): void {
    const config: SliderConfig = VITAL_FLAG_SLIDER_CONFIGS[type] || {
      rows: [{ label: 'Measures', secondary: false }],
      min: 0,
      max: 100,
      step: 1,
    };

    this.sliderRows = config.rows.map((rowConfig) => {
      const mode = this.detectSliderMode(type);
      const range = config.max - config.min;
      const decimals = (config.step.toString().split('.')[1] || '').length;
      // snap() rounds initial thumb values to the nearest valid step and fixes floating point errors
      const snap = (v: number) => parseFloat((Math.round(v / config.step) * config.step).toFixed(decimals));

      // Place thumbs at 15/35/65/85% of the range (symmetric) or 25/55% (one-sided)
      const values = mode === 'symmetric'
        ? [
            snap(config.min + range * 0.15),
            snap(config.min + range * 0.35),
            snap(config.min + range * 0.65),
            snap(config.min + range * 0.85),
          ]
        : [
            snap(config.min + range * 0.25),
            snap(config.min + range * 0.55),
          ];

      return {
        label: rowConfig.label,
        secondary: rowConfig.secondary,
        values,
        min: config.min,
        max: config.max,
        step: config.step,
        mode,
      };
    });
  }

  // Pulse Ox only alerts on low values, so it uses a 2-thumb one-sided slider
  private detectSliderMode(type: string): 'symmetric' | 'low-to-high' | 'high-to-low' {
    if (type === VitalFlagCategory.PULSE_OX) {
      return 'low-to-high';
    }
    return 'symmetric';
  }

  private buildEditableTierRows(): void {
    this.editableTierRows = [
      this.createTierRow(3), // start with one Tier 3 (High Risk) row
    ];
  }

  private createTierRow(tier: number): EditableTierRow {
    const def = this.tierDefinitions.find((td) => td.tier === tier) || this.tierDefinitions[0];
    return {
      ...def,
      offset_amount: 0,
      count: 1,
      offset_type: VitalFlagOffsetTypes.PLUS as string,
      operator: VitalFlagOperators.GREATER_EQUAL as string,
    };
  }

  private async saveFixedFlags(type: string, isSecondaryValueSystolic: boolean): Promise<void> {
    const tierNotifMap = this.getTierNotifMap();
    const config = VITAL_FLAG_SLIDER_CONFIGS[type];
    const step = config ? config.step : 1;
    const flags: any[] = [];

    for (const row of this.sliderRows) {
      // Blood Pressure has two sliders (Systolic/Diastolic), each row carries its own secondary flag.
      // For all other types, derive secondary from the form value.
      const secondary = type === VitalFlagCategory.BLOOD_PRESSURE
        ? row.secondary
        : (isSecondaryValueSystolic ? false : true);
      const records = this.buildFixedRecords(row, step, tierNotifMap);

      for (const record of records) {
        flags.push({
          type,
          secondary,
          comparitor: VitalFlagComparitors.FIXED,
          comparitor_type: 'number',
          priority: 'max',
          count: 0,
          active: true,
          ...record,
        });
      }
    }

    await this.vitalFlagService.batchSaveVitalFlags(flags);
  }

  // Converts slider thumb positions into individual Firestore flag records.
  // Each mode produces a different number of records:
  //   symmetric  → 6 records (Tier3-low, Tier2-low, Tier1-low, Tier1-high, Tier2-high, Tier3-high)
  //   low-to-high → 3 records (e.g. Pulse Ox: only low-end alerts)
  //   high-to-low → 3 records (only high-end alerts)
  // Tier 1 boundaries are auto-calculated as one step inside the Tier 2 thumbs.
  private buildFixedRecords(
    row: SliderRow,
    step: number,
    tierNotifMap: Map<number, { alert_provider: boolean; alert_patient: boolean }>
  ): Array<{ tier: number; operator: string; offset_amount: number; offset_type: string; alert_provider: boolean; alert_patient: boolean }> {
    const records: Array<{
      tier: number;
      operator: string;
      offset_amount: number;
      offset_type: string;
      alert_provider: boolean;
      alert_patient: boolean;
    }> = [];

    const getNotif = (tier: number) => tierNotifMap.get(tier) || { alert_provider: false, alert_patient: false };
    const decimals = (step.toString().split('.')[1] || '').length;
    // fix() prevents floating point drift when adding/subtracting the step value
    const fix = (v: number) => parseFloat(v.toFixed(decimals));

    if (row.mode === 'symmetric') {
      const [t3l, t2l, t2h, t3h] = row.values;
      records.push(
        { tier: 3, operator: VitalFlagOperators.LESS_EQUAL, offset_amount: t3l, offset_type: VitalFlagOffsetTypes.PLUS, ...getNotif(3) },
        { tier: 2, operator: VitalFlagOperators.LESS_EQUAL, offset_amount: t2l, offset_type: VitalFlagOffsetTypes.PLUS, ...getNotif(2) },
        { tier: 1, operator: VitalFlagOperators.GREATER_EQUAL, offset_amount: fix(t2l + step), offset_type: VitalFlagOffsetTypes.PLUS, ...getNotif(1) },
        { tier: 1, operator: VitalFlagOperators.LESS_EQUAL, offset_amount: fix(t2h - step), offset_type: VitalFlagOffsetTypes.PLUS, ...getNotif(1) },
        { tier: 2, operator: VitalFlagOperators.GREATER_EQUAL, offset_amount: t2h, offset_type: VitalFlagOffsetTypes.PLUS, ...getNotif(2) },
        { tier: 3, operator: VitalFlagOperators.GREATER_EQUAL, offset_amount: t3h, offset_type: VitalFlagOffsetTypes.PLUS, ...getNotif(3) }
      );
    } else if (row.mode === 'low-to-high') {
      const [t3l, t2l] = row.values;
      records.push(
        { tier: 3, operator: VitalFlagOperators.LESS_EQUAL, offset_amount: t3l, offset_type: VitalFlagOffsetTypes.PLUS, ...getNotif(3) },
        { tier: 2, operator: VitalFlagOperators.LESS_EQUAL, offset_amount: t2l, offset_type: VitalFlagOffsetTypes.PLUS, ...getNotif(2) },
        { tier: 1, operator: VitalFlagOperators.GREATER_EQUAL, offset_amount: fix(t2l + step), offset_type: VitalFlagOffsetTypes.PLUS, ...getNotif(1) }
      );
    } else if (row.mode === 'high-to-low') {
      const [t2h, t3h] = row.values;
      records.push(
        { tier: 1, operator: VitalFlagOperators.LESS_EQUAL, offset_amount: fix(t2h - step), offset_type: VitalFlagOffsetTypes.PLUS, ...getNotif(1) },
        { tier: 2, operator: VitalFlagOperators.GREATER_EQUAL, offset_amount: t2h, offset_type: VitalFlagOffsetTypes.PLUS, ...getNotif(2) },
        { tier: 3, operator: VitalFlagOperators.GREATER_EQUAL, offset_amount: t3h, offset_type: VitalFlagOffsetTypes.PLUS, ...getNotif(3) }
      );
    }

    return records;
  }

  // Saves one Firestore record per tier row. The count (number of days/measures)
  // is shared across all rows and comes from step 2.
  private async saveDaysMeasuresFlags(type: string, isSecondaryValueSystolic: boolean): Promise<void> {
    const secondary = isSecondaryValueSystolic ? false : true;
    const comparitor: string = this.secondFormGroup.get('comparitors').value;
    const count = Number(this.secondFormGroup.get('count').value);
    const tierNotifMap = this.getTierNotifMap();
    const flags: any[] = [];

    for (const tierRow of this.editableTierRows) {
      const notif = tierNotifMap.get(tierRow.tier) || { alert_provider: false, alert_patient: false };
      flags.push({
        type,
        secondary,
        comparitor,
        comparitor_type: 'number',
        priority: 'max',
        count,
        active: true,
        tier: tierRow.tier,
        operator: tierRow.operator,
        offset_amount: tierRow.offset_amount,
        offset_type: tierRow.offset_type,
        ...notif,
      });
    }

    await this.vitalFlagService.batchSaveVitalFlags(flags);
  }

  // Builds a lookup map from tier number → notification toggles for use during save
  private getTierNotifMap(): Map<number, { alert_provider: boolean; alert_patient: boolean }> {
    const map = new Map<number, { alert_provider: boolean; alert_patient: boolean }>();
    for (const tn of this.tierNotifications) {
      map.set(tn.tier, { alert_provider: tn.alert_provider, alert_patient: tn.alert_patient });
    }
    return map;
  }
}
